import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Button } from "semantic-ui-react";
import { Hotels, LRV, RoomClasses } from "../../api/services/next";
import { onUpdateGridHandler } from '../../components/GridPanel/utility';
import { dateFormat, dateTimeFormat, MenuActions } from "../../components/shared/constants";
import HotelsDropDown from "../../components/shared/HotelsDropDown";
import { onError, onSuccess, onWarning } from "../../components/shared/messages";
import NumericInput from '../../components/shared/NumericInput';
import RoomClassesDropDown from "../../components/shared/RoomClassesDropDown";
import { formatValueAsCurrency } from "../../components/shared/utils";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import { useMessageResources, useTextResources } from "../../language";
import GridSearchTemplate from '../GridSearchTemplate';
import BulkUpdateLrvPopup from './BulkUpdateLrvPopup';
import "./style.css";
import { DatePicker } from "../../components/shared/DatePicker";
import { useLoader } from "../../hooks/useLoader";

export const LRVPage = () => {
    const currentHotel = useCurrentHotel();
    const [search, setSearch] = useState({
        FromDate: moment().startOf("month").toDate(),
        ToDate: moment().endOf("month").toDate(),
        RoomClassIds: undefined,
        HotelIds: currentHotel ? [currentHotel?.Id] : []
    })

    const [data, setData] = useState([])
    const [hotels, setHotels] = useState([]);
    const [roomClasses, setRoomClasses] = useState([]);

    const doSearch = useRef();
    const message = useMessageResources();
    const loader = useLoader();
    const text = useTextResources();

    useEffect(() => {
        (async () => {
            try {
                loader(true);
                let hotels = await Hotels.GetHotels();
                let roomClasses = await RoomClasses.GetRoomClasses();

                setHotels(hotels);
                setRoomClasses(roomClasses);
            } catch (e) {
                onError(e);
            } finally {
                loader(false);
            }
        })();
    }, [])

    useEffect(() => {
        if (window.optima?.commands) {
            window.optima.commands.unsubscribe(MenuActions.save)
            window.optima.commands.subscribe(MenuActions.save, save);
        }
    }, [data])

    const save = () => {
        (async () => {
            try {
                loader(true)

                console.log("save data: ", data)
                await LRV.UpdateHurdleValue(data)
                onSuccess()
            } catch (error) {
                onError(error)
            } finally {
                loader(false)
            }
        })()
    }

    const formatDate = ({ value }) => {
        return value ? moment(value).format(dateFormat) : "";
    };

    const formatDateTime = ({ value }) => {
        return value ? moment(value).format(dateTimeFormat) : "";
    };

    const validations = spec => {
        if (spec.FromDate > spec.ToDate) {
            onWarning(message("ToDateGreaterThenFromDate"))
            return false
        }
        return true
    }

    const formatCurrencyValue = useCallback(({ row, column, value }) => {
        if (row && row[column.name] !== undefined) {
            return formatValueAsCurrency(row[column.name], row.CurrencyId);
        }
        return value !== undefined ? value : '-';
    }, []);

    const onGridUpdate = async (added, changed, deleted) => {
        let updated = onUpdateGridHandler(data, added, changed, deleted);
        setData(updated)
    }

    const onSearchComplete = (data) => {
        setData(data)
    }

    const onSearchChange = spec => {
        setSearch(prev => ({
            ...prev,
            ...spec
        }))
    }

    const handleBulkUpdateSave = () => {
        // Refresh the grid after bulk update
        if (doSearch.current) {
            doSearch.current();
        }
    };

    const gridOptions = {
        search: {
            title: text("LRV"),
            fields: [
                <Form.Field label={text('Hotels')} placeholder={text('All')} multiple control={HotelsDropDown} key="HotelIds" clearable />,
                <DatePicker clearable label={text('StartDate')} key='FromDate' />,
                <DatePicker clearable label={text('EndDate')} key='ToDate' />,
                <Form.Field label={text('RoomClasses')} placeholder={text('All')} multiple control={RoomClassesDropDown} key="RoomClassIds" clearable />,
            ],
            initial: search,
            spec: search,
            validate: validations,
            method: 'POST',
            api: LRV.GetLrv,
            setDoSearchRef: ref => doSearch.current = ref,
            searchOnLoad: true,
        },
        grid: {
            columns: [
                {
                    name: 'HotelId',
                    title: text("Hotel"),
                    formatter: ({ value }) => hotels.find(hotel => hotel.Id === value)?.Name || ""
                },
                {
                    name: 'RoomClassId',
                    title: text("RoomClass"),
                    formatter: ({ value }) => roomClasses.find(roomClass => roomClass.Id === value)?.Name || ""
                },
                {
                    name: 'Date',
                    title: text("Date"),
                    formatter: formatDate
                },
                {
                    name: 'HurdleValue',
                    title: text("LRVHurdleValue"),
                    formatter: formatCurrencyValue,
                    editor: <NumericInput digits={2} min={0} max={999999} />
                },
                {
                    name: 'DeltaValue',
                    title: text("LRVDelta"),
                    formatter: formatCurrencyValue
                },
                {
                    name: 'Ceiling',
                    title: text("LRVCeiling"),

                },
                {
                    name: 'MaxSold',
                    title: text("LRVMaxSold"),

                },
                {
                    name: 'Counter',
                    title: text("LRVCounter"),

                },
                {
                    name: 'LastUpdate',
                    title: text("Updated"),
                    formatter: formatDateTime
                },
            ],
            cacheId: 'LRVPage',
            customOptions: {
                group: {},
                sort: {}
            },
            paging: true,
            editable: {
                allowAdd: true,
                addButtonText: text('Update'),
                inline: true,
                modalEditor: {
                    modal: BulkUpdateLrvPopup,
                    props: {
                        defaultHotelId: currentHotel?.Id,
                        onSave: handleBulkUpdateSave
                    }
                }
            },
            columnExtensions: [
                {
                    columnName: 'HotelId',
                    editingEnabled: false,
                },
                {
                    columnName: 'RoomClassId',
                    editingEnabled: false,
                },
                {
                    columnName: 'Date',
                    editingEnabled: false,
                },
                {
                    columnName: 'HurdleValue',
                    editingEnabled: true,
                },
                {
                    columnName: 'DeltaValue',
                    editingEnabled: false,
                },
                {
                    columnName: 'Ceiling',
                    editingEnabled: false,
                },
                {
                    columnName: 'MaxSold',
                    editingEnabled: false,
                },
                {
                    columnName: 'Counter',
                    editingEnabled: false,
                },
                {
                    columnName: 'LastUpdate',
                    editingEnabled: false,
                }
            ]
        }
    }

    return (
        <GridSearchTemplate
            options={gridOptions}
            onSearchChange={onSearchChange}
            onGridUpdate={onGridUpdate}
            onSearchComplete={onSearchComplete}
            data={data}
        />
    )
};